// src/pages/SowDraftPage.jsx
// Enhanced with Version Management and Salesforce Push
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axiosClient, { sowDraftApi as api, getApiError } from "../api/client";
import { getGoogleDriveAccessToken, uploadDocxAsGoogleDoc } from "../utils/googleDrivePublish";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
import "../styles/sowDraft.css";

// ===== Modal Component =====
function Modal({ open, title, onClose, children, width = 900 }) {
  if (!open) return null;
  return (
    <div className="sd-modal" onMouseDown={onClose}>
      <div className="sd-modal-inner" style={{ maxWidth: width }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="sd-row sd-between">
          <div className="sd-modal-title">{title}</div>
          <button className="sd-btn ghost" onClick={onClose}>×</button>
        </div>
        <div className="sd-modal-body">{children}</div>
      </div>
    </div>
  );
}

// ===== Salesforce Push Modal =====
function SalesforcePushModal({ open, onClose, draft, opportunity, currentVersion, onPush }) {
  const [docType, setDocType] = useState("docx");
  const [pushing, setPushing] = useState(false);
  const [result, setResult] = useState(null);
  const [sfStatus, setSfStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  useEffect(() => {
    if (open && draft) {
      setLoadingStatus(true);
      setResult(null);
      axiosClient.get(`/sow-drafts/${draft.id}/salesforce-status`)
        .then(res => setSfStatus(res.data))
        .catch(err => setSfStatus({ error: getApiError(err) }))
        .finally(() => setLoadingStatus(false));
    }
  }, [open, draft]);

  const handlePush = async () => {
    setPushing(true);
    setResult(null);
    try {
      const res = await onPush(docType);
      setResult(res);
    } catch (e) {
      setResult({ success: false, message: e.message || String(e) });
    } finally {
      setPushing(false);
    }
  };

  if (!open) return null;

  const hasSfId = sfStatus?.has_sf_id;
  const isConnected = sfStatus?.connection?.connected;
  const isConfigured = sfStatus?.salesforce_configured;

  return (
    <Modal open={open} title="Push to Salesforce" onClose={onClose} width={550}>
      <div>
        {loadingStatus ? (
          <div className="sd-center" style={{ padding: 30 }}>
            <div className="sd-spinner"></div>
            <span>Checking Salesforce connection...</span>
          </div>
        ) : !isConfigured ? (
          <div className="sd-alert sd-alert-error">
            <span>⚠️ Salesforce is not configured. Please set the SFDC environment variables.</span>
          </div>
        ) : !hasSfId ? (
          <div className="sd-alert sd-alert-error">
            <span>⚠️ This opportunity does not have a Salesforce ID. Cannot push to Salesforce.</span>
          </div>
        ) : (
          <>
            <div className={`sd-sf-status ${isConnected ? 'connected' : 'disconnected'}`}>
              <span className="sd-sf-status-icon">{isConnected ? '✓' : '✗'}</span>
              <span>{isConnected ? 'Connected to Salesforce' : 'Not connected'}</span>
            </div>

            <div className="sd-form-group">
              <label className="sd-label">Salesforce Opportunity ID</label>
              <input className="sd-input" value={sfStatus?.sf_opportunity_id || ''} disabled />
            </div>

            <div className="sd-form-group">
              <label className="sd-label">Document Version</label>
              <input className="sd-input" value={`v${currentVersion}`} disabled />
            </div>

            <div className="sd-form-group">
              <label className="sd-label">Document Type</label>
              <select className="sd-select" value={docType} onChange={(e) => setDocType(e.target.value)}>
                <option value="docx">Word Document (.docx)</option>
                <option value="pdf" disabled>PDF Document (.pdf) - Coming soon</option>
              </select>
            </div>

            {result && (
              <div className={`sd-alert sd-alert-${result.success ? 'success' : 'error'}`} style={{ marginTop: 16 }}>
                {result.success ? '✓ ' : '✗ '}{result.message}
                {result.success && result.doc_name && (
                  <div style={{ marginTop: 8, fontSize: 12 }}>Document: {result.doc_name}</div>
                )}
              </div>
            )}

            <div className="sd-modal-actions">
              <button className="sd-btn ghost" onClick={onClose}>Cancel</button>
              <button className="sd-btn primary" onClick={handlePush} disabled={pushing || !isConnected}>
                {pushing ? "Pushing..." : "☁️ Push to Salesforce"}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ===== Version History Modal =====
function VersionHistoryModal({ open, onClose, draftId, currentVersion, onRestore }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (open && draftId) {
      setLoading(true);
      axiosClient.get(`/sow-drafts/${draftId}/versions`)
        .then(res => setVersions(res.data?.versions || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, draftId]);

  const handleRestore = async (versionId) => {
    if (!window.confirm("Restore this version? Your current changes will be saved as a new version first.")) return;
    setRestoring(true);
    try {
      await onRestore(versionId);
      onClose();
    } finally {
      setRestoring(false);
    }
  };

  const formatAction = (action) => {
    const actionMap = {
      'created': '📄 Created',
      'generated': '🤖 Generated',
      'edited': '✏️ Edited',
      'regenerated': '🔄 Regenerated',
      'restored': '⏪ Restored',
    };
    return actionMap[action] || action;
  };

  if (!open) return null;

  return (
    <Modal open={open} title="Version History" onClose={onClose} width={650}>
      <div>
        <div className="sd-version-current">Current Version: <strong>v{currentVersion}</strong></div>
        
        {loading ? (
          <div className="sd-center" style={{ padding: 40 }}><div className="sd-spinner"></div><span>Loading versions...</span></div>
        ) : versions.length === 0 ? (
          <div className="sd-center" style={{ padding: 40 }}><p className="sd-muted">No version history available yet.</p></div>
        ) : (
          <div className="sd-version-list">
            {[...versions].reverse().map((v, idx) => {
              const isCurrent = idx === 0;
              return (
                <div key={v.version_id} className={`sd-version-item ${isCurrent ? 'current' : ''}`}>
                  <div className="sd-version-info">
                    <div className="sd-version-header">
                      <span className="sd-version-number">v{v.version}</span>
                      {isCurrent && <span className="sd-badge primary">Current</span>}
                    </div>
                    <div className="sd-version-meta">
                      <span>{new Date(v.created_at).toLocaleString()}</span>
                      <span className="sd-version-action">{formatAction(v.action)}</span>
                    </div>
                    {v.sections_count && <div className="sd-version-sections">{v.sections_count} sections</div>}
                  </div>
                  <button className="sd-btn ghost sm" onClick={() => handleRestore(v.version_id)} disabled={restoring || isCurrent}>
                    {isCurrent ? "Current" : "Restore"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ===== Helpers =====
function normalizeList(res) {
  const r = res?.data ?? res;
  if (Array.isArray(r)) return r;
  if (Array.isArray(r?.items)) return r.items;
  return [];
}

function formatCurrency(amt) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amt || 0);
}

function formatDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
}

const CONTRACT_TYPES = { FIXED_BID: 'Fixed Bid', TM: 'T&M', MANAGED: 'Managed Services' };

function detectContractType(type) {
  const t = (type || "").toLowerCase();
  if (t.includes("fixed") || t.includes("bid")) return CONTRACT_TYPES.FIXED_BID;
  if (t.includes("t&m") || t.includes("time") || t.includes("material")) return CONTRACT_TYPES.TM;
  if (t.includes("managed") || t.includes("service")) return CONTRACT_TYPES.MANAGED;
  return CONTRACT_TYPES.TM;
}

const LEVELS = ["Junior", "Mid", "Senior", "Lead", "Architect", "Manager", "Director"];
const LOCATIONS = ["Onshore", "Offshore", "Nearshore", "Remote"];

// ===== Working Days & Auto-Calculation Helpers =====
function calculateWorkingDays(startDate, endDate) {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) return 0;
  let count = 0;
  let current = new Date(start);
  while (current <= end) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) count++;
    current.setDate(current.getDate() + 1);
  }
  return count;
}

function calculateHoursFromDates(startDate, endDate) {
  return calculateWorkingDays(startDate, endDate) * 8;
}

// ===== JOSS API Response Mapping =====
function formatJossDate(dateStr) {
  if (!dateStr) return "";
  try {
    const s = String(dateStr).trim();
    // Handle DD-MM-YY format (e.g. "22-06-25")
    const ddmmyy = s.match(/^(\d{2})-(\d{2})-(\d{2})$/);
    if (ddmmyy) {
      const [, dd, mm, yy] = ddmmyy;
      const year = parseInt(yy) < 50 ? `20${yy}` : `19${yy}`;
      return `${year}-${mm}-${dd}`;
    }
    // Handle ISO or other standard formats
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toISOString().split("T")[0];
  } catch {
    return String(dateStr);
  }
}

function mapJossToResources(data) {
  const items = data?.items || data?.resources || data?.data || (Array.isArray(data) ? data : []);
  return items.map(item => {
    const startDate = formatJossDate(item.START_DATE || item.start_date || item.FROM_DATE || item.from_date || "");
    const endDate = formatJossDate(item.END_DATE || item.end_date || item.TO_DATE || item.to_date || "");
    let hours = parseFloat(item.QUANTITY || item.quantity || item.ESTIMATED_HOURS || item.HOURS || item.hours || item.TOTAL_HOURS || item.total_hours || 0) || 0;
    if (!hours && startDate && endDate) {
      hours = calculateHoursFromDates(startDate, endDate);
    }
    return {
      role: item.ROLE || item.RESOURCE_ROLE || item.role || item.RESOURCE_NAME || item.resource_name || item.JOB_TITLE || item.job_title || "",
      level: item.PROFILE_CODE || item.profile_code || item.DESIGNATION || item.LEVEL || item.level || item.designation || item.GRADE || item.grade || "",
      location: item.PREMIUM_LOCATION_FLG || item.premium_location_flg || item.LOCATION || item.location || item.RESOURCE_LOCATION || item.resource_location || "",
      count: parseInt(item.COUNT || item.count || item.RESOURCE_COUNT || item.resource_count || item.QTY || item.qty || 1) || 1,
      hours,
      rate: parseFloat(item.ADJ_BILL_RATE || item.adj_bill_rate || item.BILL_RATE || item.RATE || item.rate || item.HOURLY_RATE || item.hourly_rate || item.BILLING_RATE || 0) || 0,
      startDate,
      endDate,
      technology: item.TECHNOLOGY || item.technology || "",
      domain: item.DOMAIN || item.domain || "",
      sellingAmount: parseFloat(item.SELLING_AMOUNT || item.selling_amount || 0) || 0,
      status: item.STATUS || item.status || "",
    };
  });
}

// ===== Main Component =====
export default function SowDraftPage() {
  const [step, setStep] = useState(1);
  const [opps, setOpps] = useState([]);
  const [oppSearch, setOppSearch] = useState("");
  const [oppLoading, setOppLoading] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [draftsMap, setDraftsMap] = useState({});
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const [projectDetails, setProjectDetails] = useState({
    projectName: "", projectContext: "", inScope: "", outOfScope: "",
    assumptions: "", deliverables: "", startDate: "", endDate: "",
    milestones: [{ name: "", deliverable: "", dueDate: "", percentage: 0 }],
    monthlyFee: 0, duration: 12,
    resources: [{ role: "", level: "", location: "", count: 1, hours: 0, rate: 0, startDate: "", endDate: "", technology: "", domain: "", sellingAmount: 0, status: "" }],
  });

  // JOSS Resource fetch state
  const [jossLoading, setJossLoading] = useState(false);

  // Proposal document upload state
  const [proposalFiles, setProposalFiles] = useState([]);
  const [proposalParsed, setProposalParsed] = useState(null);
  const [proposalUploading, setProposalUploading] = useState(false);
  const [proposalError, setProposalError] = useState("");

  const [draft, setDraft] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState({ current: 0, total: 0, section: "", percent: 0 });
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [regenModalOpen, setRegenModalOpen] = useState(false);
  const [regenInstructions, setRegenInstructions] = useState("");
  const [publishingGdoc, setPublishingGdoc] = useState(false);
  const [gdocLink, setGdocLink] = useState("");
  
  // Version and Salesforce state
  const [currentVersion, setCurrentVersion] = useState("1.0");
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [sfModalOpen, setSfModalOpen] = useState(false);

  const contractType = useMemo(() => detectContractType(selectedOpp?.contract_type), [selectedOpp]);
  
  const filteredOpps = useMemo(() => {
    if (!oppSearch.trim()) return opps;
    const q = oppSearch.toLowerCase();
    return opps.filter(o =>
      (o.opportunity_name || "").toLowerCase().includes(q) ||
      (o.account_name || "").toLowerCase().includes(q) ||
      (o.contract_type || "").toLowerCase().includes(q) ||
      (o.stage || "").toLowerCase().includes(q) ||
      (o.sf_opportunity_id || "").toLowerCase().includes(q)
    );
  }, [opps, oppSearch]);

  const resourceTotals = useMemo(() => {
    let totalHours = 0, totalAmount = 0;
    const breakdown = projectDetails.resources.map(r => {
      const amount = r.sellingAmount || (r.count || 1) * (r.hours || 0) * (r.rate || 0);
      totalHours += (r.count || 1) * (r.hours || 0);
      totalAmount += amount;
      return { ...r, amount };
    });
    return { breakdown, totalHours, totalAmount };
  }, [projectDetails.resources]);

  const milestoneTotals = useMemo(() => {
    const total = projectDetails.milestones.reduce((sum, m) => sum + (parseFloat(m.percentage) || 0), 0);
    return { total, isValid: Math.abs(total - 100) < 0.01 };
  }, [projectDetails.milestones]);

  const grandTotal = useMemo(() => {
    if (contractType === CONTRACT_TYPES.MANAGED) {
      return (projectDetails.monthlyFee || 0) * (projectDetails.duration || 1);
    }
    return resourceTotals.totalAmount;
  }, [contractType, projectDetails, resourceTotals]);

  const selectedSection = useMemo(() => {
    const secs = draft?.sections_json || [];
    return secs.find(s => s.id === selectedSectionId) || secs[0];
  }, [draft, selectedSectionId]);

  const orderedSections = useMemo(() => {
    return [...(draft?.sections_json || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [draft]);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    if (type === "success") setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  // Load Version Info
  const loadVersionInfo = useCallback(async () => {
    if (!draft) return;
    try {
      const res = await axiosClient.get(`/sow-drafts/${draft.id}/current-version`);
      setCurrentVersion(res.data?.current_version || "1.0");
    } catch (e) {
      console.error("Failed to load version:", e);
    }
  }, [draft]);

  useEffect(() => {
    if (draft) loadVersionInfo();
  }, [draft, loadVersionInfo]);

  // Load Opportunities and Drafts
  useEffect(() => {
    let ignore = false;
    (async () => {
      setOppLoading(true);
      try {
        const res = await api.listOpps("");
        const oppList = normalizeList(res);
        if (!ignore) setOpps(oppList);
        
        try {
          const draftsRes = await api.listDrafts?.() || [];
          const draftsList = normalizeList(draftsRes);
          const map = {};
          draftsList.forEach(d => {
            if (d.opportunity_id) {
              if (!map[d.opportunity_id] || (d.id > map[d.opportunity_id].id)) {
                map[d.opportunity_id] = d;
              }
            }
          });
          if (!ignore) setDraftsMap(map);
        } catch (e) {
          console.log("Could not load drafts list:", e);
        }
      } catch (e) {
        if (!ignore) showMsg("error", getApiError(e));
      } finally {
        if (!ignore) setOppLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const fetchTemplates = useCallback(async () => {
    if (!selectedOpp) return;
    try {
      const ct = (selectedOpp.contract_type || "").trim();
      let res = api.listTemplatesV2 ? await api.listTemplatesV2("", ct) : await api.listTemplates?.(ct);
      let list = normalizeList(res);
      if (!list.length && ct) {
        res = api.listTemplatesV2 ? await api.listTemplatesV2("", "") : await api.listTemplates?.("");
        list = normalizeList(res);
      }
      setTemplates(list);
      if (list.length === 1) setSelectedTemplateId(String(list[0].id));
    } catch (e) {
      showMsg("error", getApiError(e));
    }
  }, [selectedOpp]);

  useEffect(() => {
    if (selectedOpp) {
      fetchTemplates();
      setProjectDetails(prev => ({
        ...prev,
        projectName: selectedOpp.opportunity_name || "",
        startDate: selectedOpp.project_start_date || "",
      }));
    }
  }, [selectedOpp, fetchTemplates]);

  const selectOpportunity = (opp) => {
    setSelectedOpp(opp);
    setDraft(null);
    setSelectedTemplateId("");
    setGdocLink("");
    setCurrentVersion("1.0");
    setStep(2);
  };

  const loadExistingDraft = async (opp) => {
    const existingDraft = draftsMap[opp.id];
    if (!existingDraft) return;
    
    setBusy(true);
    try {
      const d = await api.getDraft(existingDraft.id);
      setSelectedOpp(opp);
      setDraft(d);
      setSelectedSectionId(d?.sections_json?.[0]?.id || null);
      setStep(3);
      showMsg("success", "Loaded existing draft");
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const goBack = () => {
    if (step === 2) { setStep(1); setSelectedOpp(null); }
    else if (step === 3) setStep(2);
  };

  const buildUserInputs = () => {
    const base = {
      project_name: projectDetails.projectName || selectedOpp?.opportunity_name,
      project_context: projectDetails.projectContext,
      in_scope: projectDetails.inScope,
      scope: projectDetails.inScope,
      out_of_scope: projectDetails.outOfScope,
      assumptions: projectDetails.assumptions,
      deliverables: projectDetails.deliverables,
      start_date: projectDetails.startDate || selectedOpp?.project_start_date,
      end_date: projectDetails.endDate,
      client_name: selectedOpp?.account_name,
      contract_type: contractType,
      total_amount: grandTotal,
      total_hours: resourceTotals.totalHours,
      resource_plan: resourceTotals.breakdown.map(r =>
        `${r.role} (${r.level || "N/A"}, ${r.location || "N/A"}${r.technology ? `, ${r.technology}` : ""}${r.domain ? `, ${r.domain}` : ""}): ${r.count} × ${r.hours} hrs × ${formatCurrency(r.rate)}/hr = ${formatCurrency(r.amount)}${r.startDate && r.endDate ? ` [${r.startDate} to ${r.endDate}]` : ""}${r.status ? ` (${r.status})` : ""}`
      ).join("\n"),
      resources: resourceTotals.breakdown,
      proposal_context: proposalParsed?.combined_text || "",
    };

    if (contractType === CONTRACT_TYPES.FIXED_BID) {
      const validMilestones = projectDetails.milestones.filter(m => m.name);
      return {
        ...base,
        pricing_model: "Fixed Bid",
        milestones: validMilestones,
        milestone_summary: validMilestones.map((m, i) =>
          `${i + 1}. ${m.name} - ${m.deliverable} (${m.percentage}%) - Due: ${m.dueDate || 'TBD'} - ${formatCurrency(grandTotal * (m.percentage || 0) / 100)}`
        ).join("\n"),
      };
    } else if (contractType === CONTRACT_TYPES.TM) {
      return { ...base, pricing_model: "Time & Material", rate_card: resourceTotals.breakdown.map(r => `${r.role} (${r.level || "N/A"}, ${r.location || "N/A"}): ${formatCurrency(r.rate)}/hour`).join("\n") };
    } else {
      return { ...base, pricing_model: "Managed Services", monthly_fee: projectDetails.monthlyFee, duration_months: projectDetails.duration, contract_value: grandTotal };
    }
  };

  const createAndGenerate = async () => {
    if (!selectedOpp || !selectedTemplateId) { showMsg("error", "Please select a template"); return; }
    if (!projectDetails.projectContext || !projectDetails.inScope || !projectDetails.outOfScope) {
      showMsg("error", "Project Context, In Scope, and Out of Scope are required"); return;
    }

    setGenerating(true);
    setGenProgress({ current: 0, total: 0, section: "Initializing...", percent: 0 });

    try {
      const t = templates.find(x => String(x.id) === selectedTemplateId);
      const projName = projectDetails.projectName || selectedOpp.opportunity_name;
      const title = `SOW - ${selectedOpp.account_name} - ${projName}${t?.version ? ` (v${t.version})` : ""}`;

      setGenProgress({ current: 0, total: 1, section: "Creating draft...", percent: 5 });
      const d = await api.createDraft({ opportunity_id: selectedOpp.id, template_id: Number(selectedTemplateId), title });

      const sectionCount = d?.sections_json?.length || 5;
      setGenProgress({ current: 0, total: sectionCount, section: "Starting background generation...", percent: 8 });

      // Start async (background) generation — avoids 10-min timeout
      await api.generateDraftAsync(d.id, {
        contract_type: contractType,
        user_inputs: buildUserInputs(),
        rag_k: 5, rag_mode: "agentic", rag_search_type: "semantic",
      });

      // Poll for progress every 3 seconds
      const pollInterval = 3000;
      const maxPollTime = 30 * 60 * 1000; // 30 minutes max
      const startTime = Date.now();

      const pollStatus = () => new Promise((resolve, reject) => {
        const timer = setInterval(async () => {
          try {
            if (Date.now() - startTime > maxPollTime) {
              clearInterval(timer);
              reject(new Error("Generation timed out after 30 minutes"));
              return;
            }

            const status = await api.getGenerationStatus(d.id);

            // Update progress bar with real section-by-section progress
            const pct = Math.max(10, Math.min(95, status.percent || 0));
            setGenProgress({
              current: status.current || 0,
              total: status.total || sectionCount,
              section: status.section ? `Generating: ${status.section}` : "Generating...",
              percent: pct,
            });

            if (status.generation_status === "completed") {
              clearInterval(timer);
              resolve();
            } else if (status.generation_status === "error") {
              clearInterval(timer);
              reject(new Error(status.error || "Generation failed"));
            }
          } catch (pollErr) {
            console.error("Poll error:", pollErr);
            // Don't stop polling on transient network errors
          }
        }, pollInterval);
      });

      await pollStatus();

      // Fetch the completed draft
      const generated = await api.getDraft(d.id);

      setGenProgress({ current: sectionCount, total: sectionCount, section: "Complete!", percent: 100 });
      setDraftsMap(prev => ({ ...prev, [selectedOpp.id]: generated }));
      setDraft(generated);
      setSelectedSectionId(generated?.sections_json?.[0]?.id || null);
      setStep(3);
      showMsg("success", "SOW generated successfully!");
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setGenerating(false);
      setGenProgress({ current: 0, total: 0, section: "", percent: 0 });
    }
  };

  const saveSection = async () => {
    if (!draft || !selectedSection) return;
    setBusy(true);
    try {
      const d = await api.updateSection(draft.id, selectedSection.id, { content: selectedSection.content || "" });
      setDraft(d);
      await loadVersionInfo();
      showMsg("success", "Section saved!");
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const regenerateSection = async () => {
    if (!draft || !selectedSection) return;
    
    setRegenModalOpen(false);
    setGenerating(true);
    setGenProgress({ current: 0, total: 1, section: `Regenerating ${selectedSection.title}...`, percent: 30 });
    
    try {
      const d = await api.regenerateSection(draft.id, {
        section_key: selectedSection.key,
        instructions: regenInstructions || "",
        user_inputs: buildUserInputs(),
        force: true,
      });
      
      setGenProgress({ current: 1, total: 1, section: "Complete!", percent: 100 });
      setDraft(d);
      await loadVersionInfo();
      showMsg("success", "Section regenerated!");
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setGenerating(false);
      setGenProgress({ current: 0, total: 0, section: "", percent: 0 });
    }
  };

  const restoreVersion = async (versionId) => {
    if (!draft) return;
    setBusy(true);
    try {
      const res = await axiosClient.post(`/sow-drafts/${draft.id}/versions/${versionId}/restore`);
      const d = res.data;
      setDraft(d);
      await loadVersionInfo();
      showMsg("success", "Version restored!");
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const pushToSalesforce = async (docType) => {
    if (!draft) return { success: false, message: "No draft loaded" };
    
    const res = await axiosClient.post(`/sow-drafts/${draft.id}/push-to-salesforce`, { doc_type: docType });
    const data = res.data;
    
    return data;
  };

  const exportDocx = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const blob = await api.exportDocx(draft.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(draft.title || "SOW").replace(/[\\/:*?"<>|]+/g, "_")}_v${currentVersion}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showMsg("success", "Document downloaded!");
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  async function publishToGoogleDocs() {
    if (!draft) return;
    if (!GOOGLE_CLIENT_ID) { showMsg("error", "Google Client ID not configured"); return; }

    const title = prompt("Google Doc Title?", `${draft.title} v${currentVersion}`) || draft.title;
    const folderId = (prompt("Optional: Google Drive Folder ID", "") || "").trim();

    setPublishingGdoc(true);
    try {
      const token = await getGoogleDriveAccessToken(GOOGLE_CLIENT_ID);
      const docxBlob = await api.exportDocx(draft.id);
      const { webViewLink } = await uploadDocxAsGoogleDoc({ accessToken: token, docxBlob, docTitle: title, folderId: folderId || undefined, supportsAllDrives: true });
      setGdocLink(webViewLink || "");
      showMsg("success", "Published to Google Docs!");
      if (webViewLink) window.open(webViewLink, "_blank", "noopener,noreferrer");
    } catch (e) {
      showMsg("error", e?.message || String(e));
    } finally {
      setPublishingGdoc(false);
    }
  }

  const openPreview = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const res = await api.preview(draft.id);
      setPreviewHtml(res?.html || "");
      setPreviewOpen(true);
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const updateContent = (v) => {
    if (!draft || !selectedSection) return;
    setDraft({ ...draft, sections_json: draft.sections_json.map(s => s.id === selectedSection.id ? { ...s, content: v } : s) });
  };

  const addResource = () => setProjectDetails(prev => ({ ...prev, resources: [...prev.resources, { role: "", level: "", location: "", count: 1, hours: 0, rate: 0, startDate: "", endDate: "", technology: "", domain: "", sellingAmount: 0, status: "" }] }));
  const updateResource = (idx, field, value) => {
    setProjectDetails(prev => ({
      ...prev,
      resources: prev.resources.map((r, i) => {
        if (i !== idx) return r;
        const updated = { ...r, [field]: value };
        // Auto-calculate hours when start/end dates change
        if (field === "startDate" || field === "endDate") {
          if (updated.startDate && updated.endDate) {
            updated.hours = calculateHoursFromDates(updated.startDate, updated.endDate);
          }
        }
        return updated;
      }),
    }));
  };
  const removeResource = (idx) => setProjectDetails(prev => ({ ...prev, resources: prev.resources.filter((_, i) => i !== idx) }));

  const fetchJossResources = async () => {
    if (!selectedOpp?.sf_opportunity_id) {
      showMsg("error", "No Salesforce Opportunity ID available for JOSS lookup");
      return;
    }
    setJossLoading(true);
    try {
      const res = await api.fetchJossResources(selectedOpp.sf_opportunity_id);
      if (res?.success && res?.data) {
        const mapped = mapJossToResources(res.data);
        if (mapped.length > 0) {
          setProjectDetails(prev => ({ ...prev, resources: mapped }));
          showMsg("success", `Loaded ${mapped.length} resource(s) from JOSS`);
        } else {
          showMsg("error", "No resources found in JOSS for this opportunity");
        }
      } else {
        showMsg("error", "No data returned from JOSS API");
      }
    } catch (e) {
      showMsg("error", `JOSS: ${getApiError(e)}`);
    } finally {
      setJossLoading(false);
    }
  };

  const addMilestone = () => setProjectDetails(prev => ({ ...prev, milestones: [...prev.milestones, { name: "", deliverable: "", dueDate: "", percentage: 0 }] }));
  const updateMilestone = (idx, field, value) => setProjectDetails(prev => ({ ...prev, milestones: prev.milestones.map((m, i) => i === idx ? { ...m, [field]: value } : m) }));
  const removeMilestone = (idx) => setProjectDetails(prev => ({ ...prev, milestones: prev.milestones.filter((_, i) => i !== idx) }));

  // Proposal document upload handlers
  const ACCEPTED_PROPOSAL_TYPES = ".pdf,.docx,.pptx,.xlsx,.xls,.txt,.csv,.png,.jpg,.jpeg,.gif,.bmp,.tiff";

  const handleProposalFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length + proposalFiles.length > 10) {
      setProposalError("Maximum 10 files allowed");
      return;
    }
    setProposalError("");
    setProposalFiles(prev => [...prev, ...selected]);
  };

  const handleProposalDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dropped = Array.from(e.dataTransfer.files || []);
    if (dropped.length + proposalFiles.length > 10) {
      setProposalError("Maximum 10 files allowed");
      return;
    }
    setProposalError("");
    setProposalFiles(prev => [...prev, ...dropped]);
  };

  const removeProposalFile = (idx) => {
    setProposalFiles(prev => prev.filter((_, i) => i !== idx));
    // Clear parsed results if all files removed
    if (proposalFiles.length <= 1) {
      setProposalParsed(null);
    }
  };

  const uploadAndParseProposals = async () => {
    if (!proposalFiles.length) return;
    setProposalUploading(true);
    setProposalError("");
    try {
      const result = await api.parseProposals(proposalFiles);
      setProposalParsed(result);
      if (result.error_count > 0) {
        const failedFiles = (result.files || [])
          .filter(f => f.status !== "success" && f.status !== "partial")
          .map(f => `${f.filename}: ${f.message || f.status}`)
          .join("; ");
        setProposalError(
          `${result.error_count} file(s) could not be parsed${failedFiles ? ` — ${failedFiles}` : ""}`
        );
      }
    } catch (e) {
      setProposalError(getApiError(e));
      setProposalParsed(null);
    } finally {
      setProposalUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (name) => {
    const ext = (name || "").split(".").pop().toLowerCase();
    const icons = { pdf: "PDF", docx: "DOC", doc: "DOC", pptx: "PPT", ppt: "PPT", xlsx: "XLS", xls: "XLS", csv: "CSV", txt: "TXT", png: "IMG", jpg: "IMG", jpeg: "IMG", gif: "IMG", bmp: "IMG", tiff: "IMG" };
    return icons[ext] || "FILE";
  };

  return (
    <div className="sd-page">
      {/* Header */}
      <div className="sd-header">
        <div>
          <h1 className="sd-title">SOW Drafting</h1>
          <p className="sd-subtitle">
            {step === 1 && "Step 1: Select an Opportunity"}
            {step === 2 && `Step 2: Project Details - ${selectedOpp?.account_name}`}
            {step === 3 && (<>Step 3: Review & Edit<span className="sd-version-badge">v{currentVersion}</span></>)}
          </p>
        </div>
        <div className="sd-steps">
          {[1, 2, 3].map(s => (
            <div key={s} className={`sd-step ${step >= s ? 'active' : ''} ${step === s ? 'current' : ''}`}>
              <span className="sd-step-num">{s}</span>
              <span className="sd-step-label">{['Opportunity', 'Details', 'Draft'][s-1]}</span>
            </div>
          ))}
        </div>
      </div>

      {msg.text && (
        <div className={`sd-alert sd-alert-${msg.type}`}>
          {msg.text}
          <button className="sd-alert-close" onClick={() => setMsg({ type: "", text: "" })}>×</button>
        </div>
      )}

      {generating && (
        <div className="sd-progress-wrap">
          <div className="sd-progress"><div className="sd-progress-bar" style={{ width: `${genProgress.percent}%` }} /></div>
          <div className="sd-progress-info">
            <span className="sd-progress-text">{genProgress.section}</span>
            <span className="sd-progress-percent">{genProgress.percent}%</span>
          </div>
        </div>
      )}

      {/* STEP 1 */}
      {step === 1 && (
        <div className="sd-card">
          <div className="sd-opp-header">
            <h2 className="sd-card-title">Select Opportunity</h2>
            <input className="sd-input sd-opp-search" placeholder="🔍 Search..." value={oppSearch} onChange={(e) => setOppSearch(e.target.value)} />
          </div>
          <div className="sd-opp-count">Showing {filteredOpps.length} of {opps.length}</div>

          {oppLoading ? (
            <div className="sd-center" style={{ padding: 40 }}>Loading...</div>
          ) : filteredOpps.length === 0 ? (
            <div className="sd-center" style={{ padding: 40 }}>No opportunities found</div>
          ) : (
            <div className="sd-opp-list">
              {filteredOpps.map(o => {
                const hasDraft = !!draftsMap[o.id];
                return (
                  <div key={o.id} className={`sd-opp-card ${selectedOpp?.id === o.id ? 'selected' : ''}`}>
                    <div className="sd-opp-card-header">
                      <div className="sd-opp-card-title">
                        <span className="sd-opp-name">{o.opportunity_name}</span>
                        {hasDraft && <span className="sd-draft-badge">✓ Draft</span>}
                        {o.sf_opportunity_id && <span className="sd-sf-badge" title="Has Salesforce ID">SF</span>}
                      </div>
                      <div className="sd-opp-card-actions">
                        <button className="sd-btn primary sm" onClick={() => selectOpportunity(o)}>{hasDraft ? "New" : "Select"}</button>
                        {hasDraft && <button className="sd-btn ghost sm" onClick={() => loadExistingDraft(o)}>Load</button>}
                      </div>
                    </div>
                    <div className="sd-opp-card-body">
                      <div className="sd-opp-info-row">
                        <div className="sd-opp-info"><span className="sd-opp-label">Account</span><span className="sd-opp-value">{o.account_name || "—"}</span></div>
                        <div className="sd-opp-info"><span className="sd-opp-label">Contract</span><span className="sd-opp-value"><span className="sd-tag">{o.contract_type || "—"}</span></span></div>
                        <div className="sd-opp-info"><span className="sd-opp-label">Amount</span><span className="sd-opp-value sd-opp-amount">{formatCurrency(o.opportunity_amount)}</span></div>
                      </div>
                      <div className="sd-opp-info-row">
                        <div className="sd-opp-info"><span className="sd-opp-label">Stage</span><span className="sd-opp-value">{o.stage || "—"}</span></div>
                        <div className="sd-opp-info"><span className="sd-opp-label">Start</span><span className="sd-opp-value">{formatDate(o.project_start_date)}</span></div>
                        <div className="sd-opp-info"><span className="sd-opp-label">SF ID</span><span className="sd-opp-value sd-sf-id">{o.sf_opportunity_id?.substring(0, 15) || "—"}</span></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && selectedOpp && (
        <div className="sd-card">
          <div className="sd-row sd-between" style={{ marginBottom: 16 }}>
            <div>
              <h2 className="sd-card-title">Project Details</h2>
              <p className="sd-muted"><strong>{selectedOpp.account_name}</strong> | Contract: <span className="sd-tag primary">{contractType}</span></p>
            </div>
            <button className="sd-btn ghost" onClick={goBack}>← Back</button>
          </div>

          <div className="sd-form-group" style={{ maxWidth: 400 }}>
            <label className="sd-label">Template *</label>
            <select className="sd-select" value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)}>
              <option value="">Select...</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name} - {t.sections_json?.length || 0} sections</option>)}
            </select>
          </div>

          <div className="sd-section-title">Project Information</div>
          <div className="sd-form-grid">
            <div className="sd-form-group"><label className="sd-label">Project Name *</label><input className="sd-input" value={projectDetails.projectName} onChange={(e) => setProjectDetails(p => ({ ...p, projectName: e.target.value }))} /></div>
            <div className="sd-form-group"><label className="sd-label">Client</label><input className="sd-input" value={selectedOpp.account_name} disabled /></div>
            <div className="sd-form-group"><label className="sd-label">Start Date</label><input type="date" className="sd-input" value={projectDetails.startDate} onChange={(e) => setProjectDetails(p => ({ ...p, startDate: e.target.value }))} /></div>
            <div className="sd-form-group"><label className="sd-label">End Date</label><input type="date" className="sd-input" value={projectDetails.endDate} onChange={(e) => setProjectDetails(p => ({ ...p, endDate: e.target.value }))} /></div>
          </div>

          <div className="sd-section-title">Scope Details (Required)</div>
          <div className="sd-form-grid">
            <div className="sd-form-group full"><label className="sd-label">Project Context *</label><textarea className="sd-textarea" rows={4} value={projectDetails.projectContext} onChange={(e) => setProjectDetails(p => ({ ...p, projectContext: e.target.value }))} placeholder="Background, needs..." /></div>
            <div className="sd-form-group"><label className="sd-label">In Scope *</label><textarea className="sd-textarea" rows={4} value={projectDetails.inScope} onChange={(e) => setProjectDetails(p => ({ ...p, inScope: e.target.value }))} /></div>
            <div className="sd-form-group"><label className="sd-label">Out of Scope *</label><textarea className="sd-textarea" rows={4} value={projectDetails.outOfScope} onChange={(e) => setProjectDetails(p => ({ ...p, outOfScope: e.target.value }))} /></div>
            <div className="sd-form-group"><label className="sd-label">Assumptions</label><textarea className="sd-textarea" rows={3} value={projectDetails.assumptions} onChange={(e) => setProjectDetails(p => ({ ...p, assumptions: e.target.value }))} /></div>
            <div className="sd-form-group"><label className="sd-label">Deliverables</label><textarea className="sd-textarea" rows={3} value={projectDetails.deliverables} onChange={(e) => setProjectDetails(p => ({ ...p, deliverables: e.target.value }))} /></div>
          </div>

          {/* Proposal Document Upload */}
          <div className="sd-section-title">Proposal Documents (Optional)</div>
          <div className="sd-proposal-upload-area">
            <div
              className={`sd-dropzone ${proposalUploading ? "disabled" : ""}`}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={handleProposalDrop}
            >
              <div className="sd-dropzone-content">
                <div className="sd-dropzone-icon">+</div>
                <div className="sd-dropzone-text">
                  Drag & drop proposal files here, or{" "}
                  <label className="sd-dropzone-browse">
                    browse
                    <input
                      type="file"
                      multiple
                      accept={ACCEPTED_PROPOSAL_TYPES}
                      onChange={handleProposalFileSelect}
                      style={{ display: "none" }}
                      disabled={proposalUploading}
                    />
                  </label>
                </div>
                <div className="sd-dropzone-hint">
                  PDF, DOCX, PPTX, XLSX, TXT, CSV, Images — max 10 files, 50 MB each
                </div>
              </div>
            </div>

            {proposalError && (
              <div className="sd-alert sd-alert-error" style={{ marginTop: 8, marginBottom: 0 }}>
                {proposalError}
                <button className="sd-alert-close" onClick={() => setProposalError("")}>×</button>
              </div>
            )}

            {proposalFiles.length > 0 && (
              <div className="sd-proposal-file-list">
                {proposalFiles.map((f, idx) => {
                  const parsed = proposalParsed?.files?.find(pf => pf.filename === f.name);
                  return (
                    <div key={idx} className="sd-proposal-file-item">
                      <div className="sd-proposal-file-icon">{getFileIcon(f.name)}</div>
                      <div className="sd-proposal-file-info">
                        <div className="sd-proposal-file-name">{f.name}</div>
                        <div className="sd-proposal-file-meta">
                          {formatFileSize(f.size)}
                          {parsed && (
                            <span className={`sd-proposal-status ${parsed.status}`}>
                              {parsed.status === "success" ? " — Parsed" : parsed.status === "partial" ? " — Partial" : " — Error"}
                              {parsed.char_count > 0 && ` (${parsed.char_count.toLocaleString()} chars)`}
                            </span>
                          )}
                        </div>
                      </div>
                      <button className="sd-btn mini danger" onClick={() => removeProposalFile(idx)} disabled={proposalUploading}>×</button>
                    </div>
                  );
                })}

                <div className="sd-proposal-actions">
                  <button
                    className="sd-btn primary sm"
                    onClick={uploadAndParseProposals}
                    disabled={proposalUploading || proposalFiles.length === 0}
                  >
                    {proposalUploading ? "Parsing..." : proposalParsed ? "Re-parse Files" : "Parse & Extract Content"}
                  </button>
                  {proposalParsed && (
                    <span className="sd-proposal-summary">
                      {proposalParsed.success_count} file(s) parsed — {proposalParsed.total_chars?.toLocaleString()} chars extracted
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {contractType === CONTRACT_TYPES.FIXED_BID && (
            <>
              <div className="sd-section-title">Milestones<button className="sd-btn ghost sm" onClick={addMilestone}>+ Add</button></div>
              <table className="sd-table">
                <thead><tr><th>Name</th><th>Deliverable</th><th>Due</th><th style={{width:100}}>%</th><th style={{width:120}}>Amount</th><th style={{width:50}}></th></tr></thead>
                <tbody>
                  {projectDetails.milestones.map((m, idx) => (
                    <tr key={idx}>
                      <td><input className="sd-input-tbl" value={m.name} onChange={(e) => updateMilestone(idx, "name", e.target.value)} /></td>
                      <td><input className="sd-input-tbl" value={m.deliverable} onChange={(e) => updateMilestone(idx, "deliverable", e.target.value)} /></td>
                      <td><input type="date" className="sd-input-tbl" value={m.dueDate} onChange={(e) => updateMilestone(idx, "dueDate", e.target.value)} /></td>
                      <td><input type="number" className="sd-input-tbl" value={m.percentage} onChange={(e) => updateMilestone(idx, "percentage", parseFloat(e.target.value) || 0)} /></td>
                      <td className="sd-amount">{formatCurrency(grandTotal * (m.percentage || 0) / 100)}</td>
                      <td><button className="sd-btn mini danger" onClick={() => removeMilestone(idx)}>×</button></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr><td colSpan="3" className="sd-right"><strong>Total:</strong></td><td className={milestoneTotals.isValid ? "" : "sd-error"}><strong>{milestoneTotals.total.toFixed(1)}%</strong></td><td className="sd-amount"><strong>{formatCurrency(grandTotal)}</strong></td><td></td></tr></tfoot>
              </table>
            </>
          )}

          {contractType === CONTRACT_TYPES.MANAGED && (
            <>
              <div className="sd-section-title">Service Agreement</div>
              <div className="sd-form-grid">
                <div className="sd-form-group"><label className="sd-label">Monthly Fee ($)</label><input type="number" className="sd-input" value={projectDetails.monthlyFee} onChange={(e) => setProjectDetails(p => ({ ...p, monthlyFee: parseFloat(e.target.value) || 0 }))} /></div>
                <div className="sd-form-group"><label className="sd-label">Duration (Months)</label><input type="number" className="sd-input" value={projectDetails.duration} onChange={(e) => setProjectDetails(p => ({ ...p, duration: parseInt(e.target.value) || 1 }))} /></div>
                <div className="sd-form-group"><label className="sd-label">Total</label><div className="sd-value">{formatCurrency(grandTotal)}</div></div>
              </div>
            </>
          )}

          <div className="sd-section-title">
            Resource Plan
            <span style={{ display: "inline-flex", gap: 8, marginLeft: 16 }}>
              <button className="sd-btn ghost sm" onClick={addResource}>+ Add Manual</button>
              <button
                className="sd-btn primary sm"
                onClick={fetchJossResources}
                disabled={jossLoading || !selectedOpp?.sf_opportunity_id}
                title={!selectedOpp?.sf_opportunity_id ? "No Salesforce Opportunity ID available" : "Fetch resource estimates from JOSS"}
              >
                {jossLoading ? "Fetching..." : "Fetch from JOSS"}
              </button>
            </span>
          </div>
          {!selectedOpp?.sf_opportunity_id && (
            <div className="sd-alert sd-alert-info" style={{ marginBottom: 8, padding: "6px 12px", fontSize: 13 }}>
              No Salesforce Opportunity ID found. JOSS fetch is unavailable — use manual entry below.
            </div>
          )}
          <div className="sd-table-wrap">
          <table className="sd-table" style={{minWidth: 1500}}>
            <colgroup>
              <col style={{width: 120}} />{/* Role */}
              <col style={{width: 70}} />{/* Profile */}
              <col style={{width: 110}} />{/* Location */}
              <col style={{width: 120}} />{/* Technology */}
              <col style={{width: 70}} />{/* Domain */}
              <col style={{width: 45}} />{/* # */}
              <col style={{width: 140}} />{/* Start */}
              <col style={{width: 140}} />{/* End */}
              <col style={{width: 80}} />{/* Hours */}
              <col style={{width: 90}} />{/* Rate */}
              <col style={{width: 110}} />{/* Amount */}
              <col style={{width: 80}} />{/* Status */}
              <col style={{width: 36}} />{/* Delete */}
            </colgroup>
            <thead>
              <tr>
                <th>Role</th>
                <th>Profile</th>
                <th>Location</th>
                <th>Technology</th>
                <th>Domain</th>
                <th>#</th>
                <th>Start</th>
                <th>End</th>
                <th>Hours</th>
                <th>Rate</th>
                <th>Amount</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {projectDetails.resources.map((r, idx) => {
                const amt = r.sellingAmount || (r.count || 1) * (r.hours || 0) * (r.rate || 0);
                return (
                  <tr key={idx}>
                    <td><input className="sd-input-tbl" value={r.role} onChange={(e) => updateResource(idx, "role", e.target.value)} placeholder="Role" title={r.role} /></td>
                    <td><input className="sd-input-tbl" value={r.level || ""} onChange={(e) => updateResource(idx, "level", e.target.value)} placeholder="Code" title={r.level} /></td>
                    <td><input className="sd-input-tbl" value={r.location || ""} onChange={(e) => updateResource(idx, "location", e.target.value)} placeholder="Location" title={r.location} /></td>
                    <td><input className="sd-input-tbl" value={r.technology || ""} onChange={(e) => updateResource(idx, "technology", e.target.value)} placeholder="Tech" title={r.technology} /></td>
                    <td><input className="sd-input-tbl" value={r.domain || ""} onChange={(e) => updateResource(idx, "domain", e.target.value)} placeholder="Domain" title={r.domain} /></td>
                    <td><input type="number" className="sd-input-tbl" value={r.count} onChange={(e) => updateResource(idx, "count", parseInt(e.target.value) || 1)} min={1} /></td>
                    <td><input type="date" className="sd-input-tbl" value={r.startDate || ""} onChange={(e) => updateResource(idx, "startDate", e.target.value)} /></td>
                    <td><input type="date" className="sd-input-tbl" value={r.endDate || ""} onChange={(e) => updateResource(idx, "endDate", e.target.value)} /></td>
                    <td><input type="number" className="sd-input-tbl" value={r.hours} onChange={(e) => updateResource(idx, "hours", parseInt(e.target.value) || 0)} title={r.startDate && r.endDate ? "Auto-calculated from dates" : "Enter manually or set dates"} /></td>
                    <td><input type="number" className="sd-input-tbl" value={r.rate} onChange={(e) => updateResource(idx, "rate", parseFloat(e.target.value) || 0)} /></td>
                    <td className="sd-amount">{formatCurrency(amt)}</td>
                    <td>{r.status ? <span className={`sd-status-badge ${r.status.toLowerCase()}`}>{r.status}</span> : "—"}</td>
                    <td><button className="sd-btn mini danger" onClick={() => removeResource(idx)}>×</button></td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="8" className="sd-right"><strong>Total:</strong></td>
                <td><strong>{resourceTotals.totalHours.toLocaleString()}</strong></td>
                <td></td>
                <td className="sd-amount"><strong>{formatCurrency(resourceTotals.totalAmount)}</strong></td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>
          </div>

          <div className="sd-actions">
            <button className="sd-btn primary large" onClick={createAndGenerate} disabled={generating || !selectedTemplateId || !projectDetails.projectContext || !projectDetails.inScope || !projectDetails.outOfScope}>
              {generating ? "Generating..." : "🤖 Generate SOW with AI"}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && draft && (
        <>
          <div className="sd-card sd-toolbar-card">
            <div className="sd-row sd-between">
              <div>
                <h2 className="sd-card-title">{draft.title}<span className="sd-version-badge">v{currentVersion}</span></h2>
                <p className="sd-muted">Review, edit, and publish your SOW</p>
              </div>
              <div className="sd-toolbar">
                <button className="sd-btn ghost" onClick={goBack}>← Back</button>
                <button className="sd-btn ghost" onClick={() => setVersionModalOpen(true)}>📜 Versions</button>
                <button className="sd-btn ghost" onClick={openPreview} disabled={busy}>Preview</button>
                <button className="sd-btn ghost" onClick={exportDocx} disabled={busy}>Download</button>
                <button className="sd-btn ghost" onClick={publishToGoogleDocs} disabled={publishingGdoc}>{publishingGdoc ? "..." : "Google"}</button>
                <button className="sd-btn primary salesforce" onClick={() => setSfModalOpen(true)}>☁️ Salesforce</button>
              </div>
            </div>
            {gdocLink && <div className="sd-gdoc-link">📄 <a href={gdocLink} target="_blank" rel="noopener noreferrer">{gdocLink}</a></div>}
          </div>

          <div className="sd-card">
            <h3 className="sd-card-title">Sections ({orderedSections.length})</h3>
            <div className="sd-sections-row">
              {orderedSections.map((s, idx) => (
                <div key={s.id} className={`sd-section-card ${selectedSectionId === s.id ? "active" : ""}`} onClick={() => setSelectedSectionId(s.id)}>
                  <div className="sd-section-num">{idx + 1}</div>
                  <div className="sd-section-title-card">{s.title}</div>
                  <div className="sd-section-meta">
                    {s.source === "ai" && <span className="sd-badge ai">AI</span>}
                    {s.source === "user" && <span className="sd-badge user">Edited</span>}
                    {s.locked && <span className="sd-badge locked">🔒</span>}
                    {!s.content && <span className="sd-badge empty">Empty</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="sd-card">
            <div className="sd-row sd-between" style={{ marginBottom: 12 }}>
              <h3 className="sd-card-title">Edit: {selectedSection?.title || "Select"}</h3>
              {selectedSection && (
                <div className="sd-row" style={{ gap: 8 }}>
                  <button className="sd-btn ghost" onClick={() => { setRegenInstructions(""); setRegenModalOpen(true); }} disabled={generating}>🔄 Regenerate</button>
                  <button className="sd-btn primary" onClick={saveSection} disabled={busy}>Save</button>
                </div>
              )}
            </div>
            {selectedSection ? (
              <textarea className="sd-textarea-editor" value={selectedSection.content || ""} onChange={(e) => updateContent(e.target.value)} placeholder="Section content..." />
            ) : (
              <div className="sd-muted">Select a section</div>
            )}
          </div>
        </>
      )}

      {/* Modals */}
      <Modal open={previewOpen} title="Preview" onClose={() => setPreviewOpen(false)} width={1000}>
        <div className="sd-preview" dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </Modal>

      <Modal open={regenModalOpen} title={`Regenerate: ${selectedSection?.title}`} onClose={() => setRegenModalOpen(false)} width={500}>
        <div>
          <label className="sd-label">Instructions (optional)</label>
          <textarea className="sd-textarea" rows={4} value={regenInstructions} onChange={(e) => setRegenInstructions(e.target.value)} placeholder="e.g., Make it more detailed..." />
          <div className="sd-modal-actions">
            <button className="sd-btn ghost" onClick={() => setRegenModalOpen(false)}>Cancel</button>
            <button className="sd-btn primary" onClick={regenerateSection}>Regenerate</button>
          </div>
        </div>
      </Modal>

      <VersionHistoryModal open={versionModalOpen} onClose={() => setVersionModalOpen(false)} draftId={draft?.id} currentVersion={currentVersion} onRestore={restoreVersion} />
      <SalesforcePushModal open={sfModalOpen} onClose={() => setSfModalOpen(false)} draft={draft} opportunity={selectedOpp} currentVersion={currentVersion} onPush={pushToSalesforce} />
    </div>
  );
}